/**
 * MappingEditor — editor visuale per il mapping colonne sorgente → canonical schema.
 *
 * Riceve una MappingProposal dall'API (inferita automaticamente dallo Schema Inference
 * Engine) e consente all'utente di confermare, correggere o escludere ogni colonna.
 * La confidence score è mostrata come badge colorato per guidare l'attenzione.
 *
 * US-1.3: se la proposta include saved_mapping (mapping dell'upload precedente),
 * ogni riga mostra un indicatore diff:
 *   🟢 Verde   — campo identico al mapping salvato (nessuna variazione)
 *   🟡 Giallo  — campo diverso dal mapping salvato (inference ha suggerito qualcosa di diverso)
 *   🔴 Rosso   — colonna era mappata nel salvato, ora non è mappata
 *   ⬜ Grigio  — colonna nuova, non presente nel mapping salvato
 *
 * Layout: tabella a 4 colonne (nome colonna, confidenza, confronto storico, campo canonical)
 */

import { useState } from 'react'
import { clsx } from 'clsx'
import { AlertTriangle, CheckCircle2, XCircle, ChevronDown, BookMarked, Save } from 'lucide-react'
import type { MappingProposal } from '../../hooks/useMapping'
import { useSavedMapping, useCreateNamedMapping } from '../../hooks/useMapping'

// ---------------------------------------------------------------------------
// Canonical fields — sincronizzati con CANONICAL_SCHEMA (backend)
// ---------------------------------------------------------------------------

export const CANONICAL_FIELDS: { value: string; label: string; group: string }[] = [
  // Ordini / Pipeline commerciale
  { value: 'order_id',           label: 'ID Ordine',              group: 'Ordini' },
  { value: 'order_date',         label: 'Data Ordine',            group: 'Ordini' },
  { value: 'customer_id',        label: 'ID Cliente',             group: 'Ordini' },
  { value: 'customer_name',      label: 'Nome Cliente',           group: 'Ordini' },
  { value: 'order_value',        label: 'Valore Ordine (€)',      group: 'Ordini' },
  { value: 'order_status',       label: 'Stato Ordine',           group: 'Ordini' },
  { value: 'service_line',       label: 'Linea di Servizio',      group: 'Ordini' },
  // Revenue Recognition
  { value: 'recognized_amount',  label: 'Competenziato (€)',      group: 'Ricavi' },
  { value: 'recognition_date',   label: 'Data Competenza',        group: 'Ricavi' },
  { value: 'recognition_period', label: 'Periodo Competenza',     group: 'Ricavi' },
  // Costi e produzione
  { value: 'cost_amount',        label: 'Importo Costo (€)',      group: 'Costi' },
  { value: 'cost_type',          label: 'Tipo Costo',             group: 'Costi' },
  { value: 'cost_date',          label: 'Data Costo',             group: 'Costi' },
  { value: 'hours_worked',       label: 'Ore Lavorate',           group: 'Costi' },
  { value: 'hourly_rate',        label: 'Tariffa Oraria (€/h)',   group: 'Costi' },
  // Fatturazione e incassi
  { value: 'invoice_id',         label: 'ID Fattura',             group: 'Fatture' },
  { value: 'invoice_date',       label: 'Data Fattura',           group: 'Fatture' },
  { value: 'invoice_amount',     label: 'Importo Fattura (€)',    group: 'Fatture' },
  { value: 'due_date',           label: 'Scadenza Pagamento',     group: 'Fatture' },
  { value: 'paid_date',          label: 'Data Incasso',           group: 'Fatture' },
  { value: 'paid_amount',        label: 'Importo Incassato (€)',  group: 'Fatture' },
  // Contratti ricorrenti
  { value: 'contract_id',        label: 'ID Contratto',           group: 'Contratti' },
  { value: 'recurring_value',    label: 'Valore Ricorrente (€)',  group: 'Contratti' },
  { value: 'recurring_period',   label: 'Periodicità (mesi)',     group: 'Contratti' },
  { value: 'contract_start',     label: 'Inizio Contratto',       group: 'Contratti' },
  { value: 'contract_end',       label: 'Fine Contratto',         group: 'Contratti' },
  { value: 'auto_renewal',       label: 'Rinnovo Automatico',     group: 'Contratti' },
]

// Raggruppamento per la <optgroup> del select
const FIELD_GROUPS = [...new Set(CANONICAL_FIELDS.map((f) => f.group))]

// ---------------------------------------------------------------------------
// Confidence badge
// ---------------------------------------------------------------------------

function ConfidenceBadge({ score }: { score: number | undefined }) {
  if (score === undefined) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
        <XCircle size={11} />
        Non rilevata
      </span>
    )
  }
  if (score >= 0.8) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 size={11} />
        {Math.round(score * 100)}%
      </span>
    )
  }
  if (score >= 0.5) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle size={11} />
        {Math.round(score * 100)}%
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600">
      <XCircle size={11} />
      {Math.round(score * 100)}%
    </span>
  )
}

// ---------------------------------------------------------------------------
// SimilarityBadge — diff rispetto al mapping salvato (US-1.3)
// ---------------------------------------------------------------------------

type DiffStatus = 'match' | 'changed' | 'lost' | 'new'

function getDiffStatus(
  col: string,
  currentCanonical: string,
  savedMapping: Record<string, string> | null | undefined,
): DiffStatus {
  if (!savedMapping || !(col in savedMapping)) return 'new'
  const savedCanonical = savedMapping[col]
  if (!currentCanonical) return 'lost'
  if (currentCanonical === savedCanonical) return 'match'
  return 'changed'
}

function SimilarityBadge({ status }: { status: DiffStatus }) {
  if (status === 'new') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-400">
        Nuovo
      </span>
    )
  }
  if (status === 'match') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle2 size={11} />
        Uguale
      </span>
    )
  }
  if (status === 'changed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700">
        <AlertTriangle size={11} />
        Modificato
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600">
      <XCircle size={11} />
      Rimosso
    </span>
  )
}

// ---------------------------------------------------------------------------
// FieldSelect
// ---------------------------------------------------------------------------

interface FieldSelectProps {
  value: string
  onChange: (val: string) => void
  usedValues: Set<string>
  currentCol: string
}

function FieldSelect({ value, onChange, usedValues, currentCol }: FieldSelectProps) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`Mappa ${currentCol}`}
        className={clsx(
          'w-full appearance-none rounded-lg border py-2 pl-3 pr-8 text-sm',
          'bg-white text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]',
          value
            ? 'border-[--km-border]'
            : 'border-dashed border-gray-300 text-[--km-text-secondary]',
        )}
      >
        <option value="">— Non mappare —</option>
        {FIELD_GROUPS.map((group) => (
          <optgroup key={group} label={group}>
            {CANONICAL_FIELDS.filter((f) => f.group === group).map((f) => (
              <option
                key={f.value}
                value={f.value}
                disabled={usedValues.has(f.value) && f.value !== value}
              >
                {f.label}
                {usedValues.has(f.value) && f.value !== value ? ' (già usato)' : ''}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <ChevronDown
        size={14}
        className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[--km-text-secondary]"
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// MappingEditor
// ---------------------------------------------------------------------------

export interface MappingEditorProps {
  proposal: MappingProposal
  /** Chiamato con il mapping confermato dall'utente. */
  onConfirm: (mapping: Record<string, string>) => void
  /** Chiamato al click su "Annulla". */
  onCancel: () => void
  /** Disabilita i controlli durante il salvataggio. */
  isSubmitting?: boolean
  /** ID fonte dati — se presente, mostra badge "Mapping salvato" quando disponibile. */
  dataSourceId?: string | null
}

export function MappingEditor({
  proposal,
  onConfirm,
  onCancel,
  isSubmitting = false,
  dataSourceId,
}: MappingEditorProps) {
  // Stato locale: mapping attuale (modificabile dall'utente)
  const [mapping, setMapping] = useState<Record<string, string>>(
    proposal.proposed_mapping ?? {},
  )

  const { data: savedMapping } = useSavedMapping(dataSourceId)
  const createNamedMapping = useCreateNamedMapping()

  // Stato modal "Salva come template"
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [templateName, setTemplateName] = useState('')
  const [saveError, setSaveError] = useState<string | null>(null)

  const usedCanonicalFields = new Set(Object.values(mapping).filter(Boolean))

  const mappedCount = Object.values(mapping).filter(Boolean).length
  const totalCount = proposal.source_columns.length

  function handleFieldChange(sourceCol: string, canonicalField: string) {
    setMapping((prev) => ({ ...prev, [sourceCol]: canonicalField }))
  }

  function handleConfirm() {
    // Filtra solo le coppie con valore canonical non vuoto
    const clean: Record<string, string> = {}
    for (const [src, canon] of Object.entries(mapping)) {
      if (canon) clean[src] = canon
    }
    onConfirm(clean)
  }

  async function handleSaveTemplate() {
    setSaveError(null)
    const clean: Record<string, string> = {}
    for (const [src, canon] of Object.entries(mapping)) {
      if (canon) clean[src] = canon
    }
    try {
      await createNamedMapping.mutateAsync({ name: templateName.trim(), mapping: clean })
      setSaveModalOpen(false)
      setTemplateName('')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Errore durante il salvataggio.'
      setSaveError(msg.includes('409') || msg.toLowerCase().includes('esiste') ? `Nome già utilizzato: "${templateName.trim()}"` : msg)
    }
  }

  return (
    <div className="rounded-xl border border-[--km-border] bg-[--km-surface] shadow-sm">
      {/* Header */}
      <div className="px-6 py-5 border-b border-[--km-border]">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-semibold text-[--km-text-primary]">
            Mappa le colonne del file
          </h2>
          {savedMapping && (
            <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium bg-violet-100 text-violet-700 whitespace-nowrap flex-shrink-0">
              <BookMarked size={12} />
              Mapping salvato disponibile
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-[--km-text-secondary]">
          Associa ogni colonna del tuo file al campo corrispondente di Koremargin.
          Le colonne evidenziate in rosso o arancione richiedono la tua attenzione.
        </p>
      </div>

      {/* Banner mapping salvato */}
      {savedMapping && (
        <div className="mx-6 mt-4 rounded-lg border border-violet-200 bg-violet-50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-2">
              <BookMarked size={16} className="mt-0.5 flex-shrink-0 text-violet-600" />
              <div>
                <p className="text-sm font-medium text-violet-800">
                  Mapping precedente disponibile
                </p>
                <p className="text-sm text-violet-700 mt-0.5">
                  Esiste un mapping confermato per questa fonte dati ({savedMapping.columns_count} colonne).
                  Puoi applicarlo come punto di partenza.
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMapping({ ...savedMapping.mapping })}
              className="flex-shrink-0 rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50 transition-colors"
            >
              Applica
            </button>
          </div>
        </div>
      )}

      {/* Warnings */}
      {proposal.warnings.length > 0 && (
        <div className="mx-6 mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
          <div className="flex gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-amber-600" />
            <div>
              <p className="text-sm font-medium text-amber-800">
                Attenzione — {proposal.warnings.length}{' '}
                {proposal.warnings.length === 1 ? 'avviso' : 'avvisi'}
              </p>
              <ul className="mt-1 space-y-0.5">
                {proposal.warnings.map((w, i) => (
                  <li key={i} className="text-sm text-amber-700">
                    • {w}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tabella mapping */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[--km-border] bg-gray-50 text-left">
              <th className="px-6 py-3 font-medium text-[--km-text-secondary] w-2/5">
                Colonna nel file
              </th>
              <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-28">
                Confidenza
              </th>
              {proposal.saved_mapping && (
                <th className="px-4 py-3 font-medium text-[--km-text-secondary] w-28">
                  Storico
                </th>
              )}
              <th className="px-4 py-3 font-medium text-[--km-text-secondary]">
                Campo Koremargin
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[--km-border]">
            {proposal.source_columns.map((col) => {
              const confidence = proposal.confidence_scores[col]
              const isUnmapped = !mapping[col]
              const diffStatus = getDiffStatus(col, mapping[col] ?? '', proposal.saved_mapping)
              return (
                <tr
                  key={col}
                  className={clsx(
                    'transition-colors',
                    isUnmapped ? 'bg-gray-50/60' : 'bg-white hover:bg-gray-50/40',
                  )}
                >
                  {/* Nome colonna sorgente */}
                  <td className="px-6 py-3">
                    <span
                      className={clsx(
                        'font-mono text-xs px-2 py-1 rounded-md',
                        isUnmapped
                          ? 'bg-gray-100 text-gray-500'
                          : 'bg-violet-50 text-violet-800',
                      )}
                    >
                      {col}
                    </span>
                  </td>

                  {/* Confidence badge */}
                  <td className="px-4 py-3">
                    <ConfidenceBadge score={confidence} />
                  </td>

                  {/* Diff vs mapping salvato (US-1.3) */}
                  {proposal.saved_mapping && (
                    <td className="px-4 py-3">
                      <SimilarityBadge status={diffStatus} />
                    </td>
                  )}

                  {/* Select canonical field */}
                  <td className="px-4 py-3 pr-6">
                    <FieldSelect
                      value={mapping[col] ?? ''}
                      onChange={(val) => handleFieldChange(col, val)}
                      usedValues={usedCanonicalFields}
                      currentCol={col}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex flex-col gap-3 px-6 py-4 border-t border-[--km-border] sm:flex-row sm:items-center sm:justify-between">
        {/* Riepilogo */}
        <p className="text-sm text-[--km-text-secondary]">
          <span
            className={clsx(
              'font-semibold',
              mappedCount === 0 ? 'text-[--km-error]' : 'text-[--km-text-primary]',
            )}
          >
            {mappedCount}
          </span>{' '}
          di{' '}
          <span className="font-semibold text-[--km-text-primary]">{totalCount}</span>{' '}
          colonne mappate
          {proposal.unmapped_columns.length > 0 &&
            ` · ${proposal.unmapped_columns.length} non rilevate`}
        </p>

        {/* Azioni */}
        <div className="flex gap-3 flex-wrap">
          {/* Salva come template — visibile solo se almeno una colonna è mappata */}
          {mappedCount > 0 && (
            <button
              type="button"
              onClick={() => { setSaveModalOpen(true); setSaveError(null) }}
              disabled={isSubmitting}
              className={clsx(
                'inline-flex items-center gap-1.5 rounded-lg border border-[--km-border] px-3 py-2 text-sm font-medium',
                'text-[--km-text-secondary] hover:bg-gray-50 transition-colors',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              <Save size={14} />
              Salva come template
            </button>
          )}
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className={clsx(
              'rounded-lg border border-[--km-border] px-4 py-2 text-sm font-medium',
              'text-[--km-text-primary] hover:bg-gray-50 transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || mappedCount === 0}
            className={clsx(
              'rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors',
              'bg-[--km-primary] hover:bg-violet-700',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          >
            {isSubmitting ? 'Salvataggio…' : 'Conferma mapping'}
          </button>
        </div>
      </div>

      {/* Modal — Salva come template */}
      {saveModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="w-full max-w-sm rounded-xl border border-[--km-border] bg-[--km-surface] p-6 shadow-xl">
            <h3 className="text-base font-semibold text-[--km-text-primary] mb-1">
              Salva come template
            </h3>
            <p className="text-sm text-[--km-text-secondary] mb-4">
              Il mapping corrente ({mappedCount} colonne) verrà salvato come template
              riutilizzabile su qualsiasi upload futuro.
            </p>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && templateName.trim() && handleSaveTemplate()}
              placeholder="Es. Formato fatture Fornitore X"
              maxLength={60}
              className={clsx(
                'w-full rounded-lg border px-3 py-2 text-sm',
                'bg-white text-[--km-text-primary] focus:outline-none focus:ring-2 focus:ring-[--km-primary]',
                saveError ? 'border-red-400' : 'border-[--km-border]',
              )}
              autoFocus
            />
            {saveError && (
              <p className="mt-1 text-xs text-red-600">{saveError}</p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => { setSaveModalOpen(false); setTemplateName(''); setSaveError(null) }}
                className="rounded-lg border border-[--km-border] px-3 py-1.5 text-sm text-[--km-text-primary] hover:bg-gray-50"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleSaveTemplate}
                disabled={!templateName.trim() || createNamedMapping.isPending}
                className={clsx(
                  'rounded-lg px-3 py-1.5 text-sm font-medium text-white',
                  'bg-[--km-primary] hover:bg-violet-700 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                {createNamedMapping.isPending ? 'Salvataggio…' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
